// frontend-react/src/pages/league-dashboard/DraftPicksMatrix.js
// Matrix view: all teams × all rounds for a draft year — V1A Sharp Contrast

import React, { useMemo } from 'react';
import { RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

/**
 * DraftPicksMatrix
 *
 * Props:
 *   data    — response from GET /draft-picks/matrix (null while loading)
 *   loading — boolean
 *   error   — string | null
 *   onRetry — () => void
 */
const DraftPicksMatrix = ({ data, loading, error, onRetry }) => {
  // Index each team's ORIGINAL picks by round for O(1) cell lookup.
  // Backend groups by original owner — exactly 1 pick per team per round.
  const teamPicksByRound = useMemo(() => {
    if (!data?.teams) return {};
    const index = {};
    data.teams.forEach(team => {
      index[team.team_id] = {};
      team.picks.forEach(pick => {
        index[team.team_id][pick.round] = pick;
      });
    });
    return index;
  }, [data]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <RefreshCw className="animate-spin" size={32} color={dynastyTheme.tokens?.colors?.primary} />
        <p className={dynastyTheme.classes.text.neutralLight}>Loading draft picks matrix...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <AlertCircle size={32} className={dynastyTheme.classes.text.error} />
        <p className={`${dynastyTheme.classes.text.error} text-sm`}>{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className={dynastyTheme.utils?.getComponent('button', 'secondary', 'sm')}
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { teams, total_rounds, year, order_is_final, num_teams } = data;

  if (!teams || teams.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className={dynastyTheme.classes.text.neutralLight}>
          No draft picks found for {year}. Draft picks are generated when the season starts.
        </p>
      </div>
    );
  }

  const rounds = Array.from({ length: total_rounds }, (_, i) => i + 1);

  const getDisplayPosition = (pick) => {
    return order_is_final ? pick.pick_in_round : pick.implied_pick_in_round;
  };

  // Truncate team name to max chars with ellipsis
  const truncName = (n, max = 11) => n && n.length > max ? n.slice(0, max) + '…' : (n || '');

  // Round tier helpers
  const isElite = (r) => r <= 3;
  const isMid = (r) => r >= 4 && r <= 8;

  const renderCell = (team, round) => {
    const pick = teamPicksByRound[team.team_id]?.[round];

    if (pick) {
      // Pick already used (selected in draft)
      if (pick.selection) {
        return (
          <div className="text-center px-1 py-1">
            <div
              className={`text-[10px] ${dynastyTheme.classes.text.neutralDark} truncate leading-tight`}
              style={{ maxWidth: '72px', margin: '0 auto' }}
              title={pick.selection.player_name}
            >
              {pick.selection.player_name}
            </div>
          </div>
        );
      }

      const pos = getDisplayPosition(pick);

      // Traded away — show the NEW OWNER's name with gold glow
      if (!pick.is_own_pick) {
        return (
          <div
            className={`cell-hover text-center rounded-md py-1.5 px-1${isElite(round) ? ' acq-glow' : ''}`}
            style={{
              background: 'linear-gradient(135deg, rgba(234,179,8,0.10), rgba(234,179,8,0.03))',
              border: '1.5px solid rgba(234,179,8,0.35)',
            }}
          >
            <div
              className={`${dynastyTheme.classes.text.primaryBright} font-black leading-none`}
              style={{
                fontSize: '22px',
                textShadow: '0 0 16px rgba(234,179,8,0.4)',
              }}
            >
              {pos ?? '—'}
            </div>
            <div
              className={`${dynastyTheme.classes.text.primary} leading-tight truncate`}
              style={{ fontSize: '9px', opacity: 0.6, marginTop: '2px', maxWidth: '70px', margin: '2px auto 0' }}
              title={`→ ${pick.current_owner?.team_name}`}
            >
              → {truncName(pick.current_owner?.team_name)}
            </div>
          </div>
        );
      }

      // Own pick — tier by round
      if (isElite(round)) {
        return (
          <div
            className="cell-hover text-center rounded-md py-1.5"
            style={{ background: 'rgba(234,179,8,0.03)', border: '1px solid rgba(234,179,8,0.10)' }}
          >
            <span
              className={`${dynastyTheme.classes.text.white} font-black leading-none`}
              style={{ fontSize: '22px', textShadow: '0 0 8px rgba(255,255,255,0.1)' }}
            >
              {pos ?? '—'}
            </span>
          </div>
        );
      }
      if (isMid(round)) {
        return (
          <div className="cell-hover text-center rounded-md py-1.5 bg-neutral-800/40">
            <span
              className="text-neutral-300 font-black leading-none"
              style={{ fontSize: '22px' }}
            >
              {pos ?? '—'}
            </span>
          </div>
        );
      }
      // Late R9+
      return (
        <div
          className="cell-hover text-center rounded-md py-1.5"
          style={{ background: '#0a0907' }}
        >
          <span
            className="text-neutral-600 font-black leading-none"
            style={{ fontSize: '22px' }}
          >
            {pos ?? '—'}
          </span>
        </div>
      );
    }

    // Empty slot (no pick record exists for this round)
    return (
      <span className="text-neutral-800 text-xs">—</span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Keyframe animations */}
      <style>{`
        @keyframes pulseGlow {
          0%,100% { box-shadow: 0 0 8px rgba(234,179,8,0.15); }
          50%      { box-shadow: 0 0 20px rgba(234,179,8,0.3); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .matrix-row {
          animation: fadeSlideIn 0.3s ease both;
        }
        .matrix-row:nth-child(1)  { animation-delay: 0.00s; }
        .matrix-row:nth-child(2)  { animation-delay: 0.04s; }
        .matrix-row:nth-child(3)  { animation-delay: 0.08s; }
        .matrix-row:nth-child(4)  { animation-delay: 0.12s; }
        .matrix-row:nth-child(5)  { animation-delay: 0.16s; }
        .matrix-row:nth-child(6)  { animation-delay: 0.20s; }
        .matrix-row:nth-child(7)  { animation-delay: 0.24s; }
        .matrix-row:nth-child(8)  { animation-delay: 0.28s; }
        .matrix-row:nth-child(9)  { animation-delay: 0.32s; }
        .matrix-row:nth-child(10) { animation-delay: 0.36s; }
        .matrix-row:nth-child(n+11) { animation-delay: 0.40s; }
        .cell-hover {
          transition: all 0.15s ease;
          position: relative;
        }
        .cell-hover:hover {
          transform: scale(1.06);
          z-index: 5;
        }
        .acq-glow {
          animation: pulseGlow 2.4s ease-in-out infinite;
        }
      `}</style>

      {/* Header card */}
      <div className={`${dynastyTheme.components.card.highlighted} p-4`}>
        <div className="flex items-center justify-between gap-4">
          {/* Left: badge + title */}
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-lg flex-shrink-0 font-black text-black"
              style={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #fde047, #eab308)',
                fontSize: '14px',
                letterSpacing: '-0.5px',
              }}
            >
              '{String(year).slice(-2)}
            </div>
            <div>
              <div className={`text-base font-bold ${dynastyTheme.classes.text.white} leading-tight`}>
                Draft Board
              </div>
              <div className={`text-xs italic ${dynastyTheme.classes.text.neutralLight} leading-tight mt-0.5`}>
                Implied order · Current standings
              </div>
            </div>
          </div>

          {/* Right: order badge + legend */}
          <div className="flex flex-col items-end gap-2">
            {order_is_final && (
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 ${dynastyTheme.classes.text.success} border border-emerald-500/40`}>
                <CheckCircle2 size={12} />
                Draft order is set
              </div>
            )}
            <div className={`flex items-center gap-4 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ background: 'rgba(234,179,8,0.03)', border: '1px solid rgba(234,179,8,0.10)' }}
                />
                Own
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{
                    background: 'linear-gradient(135deg, rgba(234,179,8,0.10), rgba(234,179,8,0.03))',
                    border: '1.5px solid rgba(234,179,8,0.35)',
                  }}
                />
                <span className={dynastyTheme.classes.text.primary}>Traded</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Matrix table */}
      <div
        className="overflow-x-auto rounded-lg"
        style={{ border: '1px solid rgba(64,64,64,0.30)', background: 'rgba(12,10,9,0.80)' }}
      >
        <table
          className="text-sm"
          style={{
            minWidth: `${total_rounds * 82 + 168}px`,
            borderCollapse: 'separate',
            borderSpacing: '4px',
            padding: '4px',
          }}
        >
          <thead>
            <tr>
              {/* Team column header */}
              <th
                className={`sticky left-0 z-10 text-left px-3 py-2 text-xs font-semibold ${dynastyTheme.classes.text.primary} uppercase tracking-widest`}
                style={{ background: '#0c0a09', minWidth: '160px' }}
              >
                Team
              </th>
              {rounds.map(r => {
                const elite = isElite(r);
                const mid = isMid(r);
                return (
                  <th
                    key={r}
                    className={`px-1 py-2 text-center text-xs font-semibold uppercase tracking-wide
                      ${elite ? `${dynastyTheme.classes.text.primary}` : mid ? 'text-neutral-400' : 'text-neutral-600'}`}
                    style={{
                      minWidth: '78px',
                      ...(elite ? {
                        borderBottom: '2px solid rgba(234,179,8,0.25)',
                        background: 'rgba(234,179,8,0.03)',
                      } : {}),
                    }}
                  >
                    R{r}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {teams.map((team, rowIdx) => (
              <tr key={team.team_id} className="matrix-row">
                {/* Team name cell — sticky */}
                <td
                  className="sticky left-0 z-10 px-3 py-2"
                  style={{
                    background: rowIdx % 2 === 0 ? '#0c0a09' : '#1c1917',
                    borderLeft: '3px solid rgba(234,179,8,0.30)',
                  }}
                >
                  <div
                    className={`text-[13px] font-bold ${dynastyTheme.classes.text.white} truncate`}
                    style={{ maxWidth: '148px' }}
                    title={team.team_name}
                  >
                    {team.team_name}
                  </div>
                  <div
                    className={`${dynastyTheme.classes.text.primary} font-semibold`}
                    style={{ fontSize: '10px', opacity: 0.8 }}
                  >
                    Pick #{rowIdx + 1}
                  </div>
                </td>

                {/* Round cells */}
                {rounds.map(r => (
                  <td
                    key={r}
                    className="text-center align-middle"
                    style={{ verticalAlign: 'middle', padding: '2px' }}
                  >
                    {renderCell(team, r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DraftPicksMatrix;
